// Subscribes to the transport's account-event stream (text/JSON frames over
// the same WS as message envelopes) and emits notification events for
// account-level changes: a new device linked to your account, or a peer
// updating their profile. Message/invite notifications come from the
// useIncomingMessages/useIncomingConversations chokepoints instead.
//
// The transport is a per-app singleton (web `getCurrentTransport`, desktop
// equivalent), so it's injected rather than imported here.

import { useEffect } from 'react';

import { log } from '../log/log';
import type { IncomingNotification } from './types';

/** Cleanup handle returned by `onAccountEvent` — accepts the transport's
 *  `SubscribeHandle` ({ unsubscribe }), a plain disposer fn, or void. */
type AccountEventHandle = { unsubscribe(): void } | { close(): void } | (() => void) | void;

interface AccountEventTransport {
  onAccountEvent?(handler: (event: Record<string, unknown>) => void): AccountEventHandle;
}

export interface UseAccountEventNotificationsOptions {
  /** The active transport (or null before bootstrap). */
  readonly transport: AccountEventTransport | null | undefined;
  readonly onNotify: (evt: IncomingNotification) => void;
}

export function useAccountEventNotifications(
  opts: UseAccountEventNotificationsOptions,
): void {
  const { transport, onNotify } = opts;

  useEffect(() => {
    if (!transport || typeof transport.onAccountEvent !== 'function') return;

    const handle = transport.onAccountEvent((event) => {
      const type = typeof event['type'] === 'string' ? (event['type'] as string) : '';
      if (type === 'device_added') {
        const label = typeof event['label'] === 'string' ? (event['label'] as string) : undefined;
        try {
          onNotify({
            kind: 'device',
            isMention: false,
            sentAt: Date.now(),
            ...(label !== undefined ? { senderName: label } : {}),
          });
        } catch (err) {
          log.warn('accountEventNotifications.device.failed', { error: (err as Error).message });
        }
      } else if (type === 'peer_profile_updated') {
        try {
          onNotify({ kind: 'profile', isMention: false, sentAt: Date.now() });
        } catch (err) {
          log.warn('accountEventNotifications.profile.failed', { error: (err as Error).message });
        }
      }
    });

    return () => {
      try {
        if (typeof handle === 'function') handle();
        else if (handle && 'unsubscribe' in handle && typeof handle.unsubscribe === 'function') {
          handle.unsubscribe();
        } else if (handle && 'close' in handle && typeof handle.close === 'function') {
          handle.close();
        }
      } catch (err) {
        log.warn('accountEventNotifications.unsubscribe.failed', {
          error: (err as Error).message,
        });
      }
    };
  }, [transport, onNotify]);
}
